import React, { useEffect, useMemo, useState, Children, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ExternalLinkIcon, LogOutIcon } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, User } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, updateDoc, setDoc, doc, getDoc, deleteDoc, arrayUnion, serverTimestamp, Timestamp } from 'firebase/firestore';
// ── Firebase Initialization ─────────────────────────────────────
const firebaseConfig = {
  apiKey: 'AIzaSyC95YAd0hECiLzZYyLj3kAf8fIjDRo6mvg',
  authDomain: 'gsfliveagency-31e40.firebaseapp.com',
  projectId: 'gsfliveagency-31e40',
  storageBucket: 'gsfliveagency-31e40.firebasestorage.app',
  messagingSenderId: '1023743622410',
  appId: '1:1023743622410:web:611f17b3063fd1c86c8ef3',
  measurementId: 'G-LBQC232NCB'
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const creatorFirebaseConfig = {
  apiKey: 'AIzaSyC95YAd0hECiLzZYyLj3kAf8fIjDRo6mvg',
  authDomain: 'gsfliveagency-31e40.firebaseapp.com',
  projectId: 'gsfliveagency-31e40',
  storageBucket: 'gsfliveagency-31e40.firebasestorage.app',
  messagingSenderId: '1023743622410',
  appId: '1:1023743622410:web:9d703a73017f16676c8ef3',
  measurementId: 'G-LYZ8QPBFTZ'
};
const creatorApp = initializeApp(creatorFirebaseConfig, 'creator');
// Safe helper to convert Firestore Timestamps, plain objects, or other date-like values to Date
function safeToDate(val: any): Date | null {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val.seconds === 'number') return new Date(val.seconds * 1000);
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') return new Date(val);
  return null;
}
const creatorAuth = getAuth(creatorApp);
const creatorDb = getFirestore(creatorApp);
// ── Sparkle Overlay ─────────────────────────────────────────────
function ActionButtons({
  currentPage,
  onNavigate
}: {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.8,
    duration: 0.5
  }} className="mt-16 mb-10 flex flex-col items-center justify-center w-full" data-id="element-0">
      <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1">
        <motion.a href="mailto:gsfliveagency@gmail.com" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] transition-all" data-id="element-2">
          Contact Us
        </motion.a>
        {currentPage !== 'contact' && <motion.button onClick={() => onNavigate('contact')} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="inline-flex items-center px-6 py-3 rounded-xl border border-white/20 bg-white/5 backdrop-blur-sm text-white font-semibold font-['Montserrat'] hover:bg-white/10 transition-all" data-id="element-3">
            Join GSF
          </motion.button>}
      </div>
    </motion.div>;
}
function SparkleOverlay() {
  const glitter = useMemo(() => {
    const items: {
      id: number;
      x: number;
      y: number;
      size: number;
      delay: number;
      duration: number;
      type: 'bright' | 'medium' | 'soft';
      color: string;
    }[] = [];
    const colors = ['rgba(200,130,255,1)', 'rgba(180,80,255,1)', 'rgba(220,150,255,1)', 'rgba(160,60,255,1)', 'rgba(190,100,255,1)', 'rgba(230,170,255,1)', 'rgba(170,90,255,1)', 'rgba(255,130,220,1)'];
    for (let i = 0; i < 300; i++) {
      const type = i < 60 ? 'bright' : i < 160 ? 'medium' : 'soft';
      items.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: type === 'bright' ? Math.random() * 3 + 2 : type === 'medium' ? Math.random() * 2.5 + 1.2 : Math.random() * 1.8 + 0.6,
        delay: Math.random() * 6,
        duration: type === 'bright' ? Math.random() * 1.5 + 1 : Math.random() * 2.5 + 1.5,
        type,
        color: colors[Math.floor(Math.random() * colors.length)]
      });
    }
    return items;
  }, []);
  return <div className="fixed inset-0 pointer-events-none z-[5]" data-id="element-4">
      {glitter.map(g => <div key={g.id} className="glitter-particle" data-type={g.type} style={{
      left: `${g.x}%`,
      top: `${g.y}%`,
      width: `${g.size}px`,
      height: `${g.size}px`,
      animationDelay: `${g.delay}s`,
      animationDuration: `${g.duration}s`,
      '--glitter-color': g.color
    } as React.CSSProperties} data-id="element-5" />)}
      {/* Light wave sweep */}
      <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '30%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent 0%, rgba(168,130,255,0.04) 30%, rgba(255,255,255,0.06) 50%, rgba(130,180,255,0.04) 70%, transparent 100%)',
      animation: 'light-wave 12s ease-in-out infinite'
    }} data-id="element-6" />
    </div>;
}
type PageId = 'home' | 'team' | 'about' | 'why' | 'services' | 'weekly-schedules' | 'schedule-prince-justice' | 'schedule-baddie' | 'schedule-star-dj' | 'schedule-nessy' | 'schedule-drika' | 'schedule-mandy' | 'schedule-zetta' | 'schedule-jdolla' | 'schedule-j3' | 'schedule-shorty' | 'contact' | 'agent-login' | 'creator-login' | 'portal-hub' | 'creator-hub' | 'portal' | 'portal-qvc' | 'creator-portal' | 'creator-portal-qvc';
const NAV_ITEMS: {
  label: string;
  page: PageId;
}[] = [{
  label: 'Home',
  page: 'home'
}, {
  label: 'About Us',
  page: 'about'
}, {
  label: 'Meet the Team',
  page: 'team'
}, {
  label: 'Why GSF',
  page: 'why'
}, {
  label: 'Services',
  page: 'services'
}, {
  label: 'Live Schedules',
  page: 'weekly-schedules'
}, {
  label: 'Join',
  page: 'contact'
}];
// ── Org Chart Data ──────────────────────────
interface OrgNode {
  id: string;
  name: string;
  title: string;
  photo?: string;
  tiktok?: string;
  size: 'owner' | 'director' | 'agent' | 'creator';
  children?: OrgNode[];
}
const ORG_DATA: OrgNode = {
  id: 'owner',
  name: 'STARPLAYER757',
  title: 'GSF Owner / Founder',
  photo: 'https://cdn.magicpatterns.com/uploads/v5aPbSvNA9zHB77CtwRwnd/file_000000007cbc71fb950c62ed89b90586.png',
  tiktok: 'https://www.tiktok.com/@zoeaking?_r=1&_t=ZP-95d2CTbttLr',
  size: 'owner',
  children: [{
    id: 'co-lead',
    name: '"Q" BigAFrememberdat',
    title: 'Assistant to CEO / Co-Lead Agent',
    photo: 'https://cdn.magicpatterns.com/uploads/ugsYfBkVtUZWuxXTsbnVba/file_00000000e44871fb959602b9223382bc.png',
    tiktok: 'https://www.tiktok.com/@bigafrememberdat',
    size: 'director',
    children: [{
      id: 'dir-1',
      name: 'BaddieDaBoss',
      title: 'Director of Coordination',
      photo: 'https://cdn.magicpatterns.com/uploads/d1X4vJ4wsQY57gLp4vuEUt/file_00000000fdd871f7917e9b43635bb53e.png',
      tiktok: 'https://www.tiktok.com/@nfskmx26?_r=1&_t=ZP-95d239RoLCZ',
      size: 'director',
      children: [{
        id: 'agt-1',
        name: 'BaddieDaBoss',
        title: 'Lead Agent',
        photo: 'https://cdn.magicpatterns.com/uploads/d1X4vJ4wsQY57gLp4vuEUt/file_00000000fdd871f7917e9b43635bb53e.png',
        tiktok: 'https://www.tiktok.com/@nfskmx26?_r=1&_t=ZP-95d239RoLCZ',
        size: 'agent',
        children: [{
          id: 'crt-1',
          name: 'NESSY™',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/sqa959qSCN7fRwwSGBVycr/ChatGPT_Image_Apr_22,_2026,_10_10_18_PM.png',
          tiktok: 'https://www.tiktok.com/@nessytherilla',
          size: 'creator'
        }]
      }, {
        id: 'agt-2',
        name: 'Trippin.with.mandy',
        title: 'GSF Agent',
        photo: 'https://cdn.magicpatterns.com/uploads/w6gKF6k398cAmKSJxkiTZK/file_00000000464871f78b5d9742c72cca65.png',
        tiktok: 'https://www.tiktok.com/@trippin.with.mandy',
        size: 'agent',
        children: [{
          id: 'crt-3',
          name: 'ZettaMINDset',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/mqTALxa5F64M2yQL8KH6YU/file_0000000036f871fdb97ff10845e446c2.png',
          tiktok: 'https://www.tiktok.com/@mszetta?_r=1&_t=ZP-95d2AQ9iXwU',
          size: 'creator'
        }, {
          id: 'crt-2',
          name: 'bigdrikababii',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/cALc11xyrw6nPdVGTeNcKF/file_000000005fbc71f89fb99a2e17e35c53.png',
          tiktok: 'https://www.tiktok.com/@bigdrikababii?_r=1&_t=ZP-95d23vNAzUc',
          size: 'creator'
        }]
      }, {
        id: 'agt-4',
        name: 'J3',
        title: 'GSF Agent',
        photo: 'https://cdn.magicpatterns.com/uploads/kHPVAQbDpnt8Zdza5GNRAF/file_00000000dfc071fdaf303e4b92a454de.png',
        tiktok: 'https://www.tiktok.com/@chyyy..0110?_r=1&_t=ZP-95d27IRtmj1',
        size: 'agent',
        children: [{
          id: 'crt-5',
          name: 'IAM_JDOLLA',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/m4k4ZRiRRre616KQMVQBsA/ChatGPT_Image_Apr_22,_2026,_10_17_55_PM.png',
          tiktok: 'https://www.tiktok.com/@jdolla_74?_r=1&_t=ZP-95d1zT2i8L9',
          size: 'creator'
        }]
      }]
    }, {
      id: 'dir-2',
      name: 'ZettaMINDset',
      title: 'Director of GSF / QVC',
      photo: 'https://cdn.magicpatterns.com/uploads/mqTALxa5F64M2yQL8KH6YU/file_0000000036f871fdb97ff10845e446c2.png',
      tiktok: 'https://www.tiktok.com/@mszetta?_r=1&_t=ZP-95d2AQ9iXwU',
      size: 'director',
      children: [{
        id: 'agt-5',
        name: 'Trippin.with.mandy',
        title: 'GSF / QVC Advisory Agent',
        photo: 'https://cdn.magicpatterns.com/uploads/w6gKF6k398cAmKSJxkiTZK/file_00000000464871f78b5d9742c72cca65.png',
        tiktok: 'https://www.tiktok.com/@trippin.with.mandy',
        size: 'agent'
      }, {
        id: 'agt-6',
        name: 'bigdrikababii',
        title: 'Live Creator',
        photo: 'https://cdn.magicpatterns.com/uploads/cALc11xyrw6nPdVGTeNcKF/file_000000005fbc71f89fb99a2e17e35c53.png',
        tiktok: 'https://www.tiktok.com/@bigdrikababii?_r=1&_t=ZP-95d23vNAzUc',
        size: 'agent'
      }]
    }]
  }]
};
// ── Org Chart Components ───────────────────────────────────────────
function getInitials(name: string) {
  const clean = name.replace(/[^a-zA-Z0-9 ]/g, ' ').trim();
  const parts = clean.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.substring(0, 2).toUpperCase();
}
function OrgCard({
  node
}: {
  node: OrgNode;
}) {
  const imageSizes = {
    owner: 'w-44 md:w-72',
    director: 'w-36 md:w-60',
    agent: 'w-32 md:w-52',
    creator: 'w-28 md:w-44'
  };
  return <motion.a href={node.tiktok || '#'} target={node.tiktok ? '_blank' : '_self'} rel={node.tiktok ? 'noopener noreferrer' : ''} initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} whileHover={{
    scale: 1.08,
    y: -5
  }} className={`flex flex-col items-center cursor-pointer group ${imageSizes[node.size]}`} data-id="element-7">
      {node.photo ? <img src={node.photo} alt={node.name} className="object-contain group-hover(drop-shadow(0 0 15px rgba(168,85,247,0.3)))" data-id="element-8" /> : <div className="w-full aspect-square bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col items-center justify-center p-3 rounded-full" data-id="element-9">
          <span className="text-white font-bold text-lg md:text-2xl tracking-wider" data-id="element-10">
            {getInitials(node.name)}
          </span>
          <span className="text-white/80 text-[8px] md:text-xs mt-1 text-center font-medium" data-id="element-11">
            {node.name}
          </span>
          <span className="text-white/50 text-[7px] md:text-[10px] text-center" data-id="element-12">
            {node.title}
          </span>
        </div>}
    </motion.a>;
}
function TreeNode({
  node,
  isRoot = false,
  isFirst = false,
  isLast = false,
  isOnly = false
}: {
  node: OrgNode;
  isRoot?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isOnly?: boolean;
}) {
  return <div className="flex flex-col items-center relative px-1 md:px-3" data-id="element-13">
      {/* Lines above the node (skip for root) */}
      {!isRoot && <>
          {/* Horizontal line connecting siblings */}
          {!isOnly && <div className={`absolute top-0 h-[2px] bg-gradient-to-r from-purple-500/60 to-blue-500/60
              ${isFirst ? 'left-1/2 right-0' : isLast ? 'left-0 right-1/2' : 'left-0 right-0'}
            `} data-id="element-14" />}
          {/* Vertical line down to card */}
          <div className="w-[2px] h-4 md:h-8 bg-gradient-to-b from-purple-500/60 to-blue-500/60" data-id="element-15" />
        </>}

      {/* The Card */}
      <OrgCard node={node} data-id="element-16" />

      {/* Lines below the node (if has children) */}
      {node.children && node.children.length > 0 && <>
          {/* Vertical line down from card */}
          <div className="w-[2px] h-4 md:h-8 bg-gradient-to-b from-purple-500/60 to-blue-500/60" data-id="element-17" />
          {/* Children container */}
          <div className="flex justify-center items-start relative" data-id="element-18">
            {node.children.map((child, idx) => <TreeNode key={child.id} node={child} isRoot={false} isFirst={idx === 0} isLast={idx === node.children.length - 1} isOnly={node.children.length === 1} data-id="element-19" />)}
          </div>
        </>}
    </div>;
}
// ── Meet the Team Page ─────────────────────────────────────────
function MeetTheTeam() {
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-20">
      {/* Section Title */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="text-center mb-12" data-id="element-21">
        <h2 className="font-['Montserrat'] font-black text-3xl md:text-5xl tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-purple-300 uppercase" data-id="element-22">
          GSF Agency Chain of Command
        </h2>
        <p className="text-white/50 mt-3 text-sm md:text-base max-w-md mx-auto" data-id="element-23">
          Tap a card to visit their TikTok profile.
        </p>
      </motion.div>

      {/* Highlight Reel */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.3
    }} className="text-center mb-16" data-id="element-24">
        <h3 className="font-['Montserrat'] font-black text-2xl md:text-3xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-8" data-id="element-25">
          🎬 Highlight Reel
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto px-2 md:px-4" data-id="element-26">
          {[
        // ── Highlight Video 1 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/j2L5HFDMJk71ujTNf9VgKn/05b56bc761ac1841cee144bc2624aa30.mp4',
        // ── Highlight Video 2 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/nca79a8eKdsccyD2PqVw67/9b1f930f693ec19f3966b3e14bc46d32.mp4',
        // ── Highlight Video 3 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/m8WkUNUJey2Xborr1k9m9c/8e4b4ec9eaf3df3cb2d789f280e7966e.mp4',
        // ── Highlight Video 4 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/snU4LBjHSricg2jA9pMfNX/77ed4a9232548fb2569a2d5f0cdbd790.mp4'].map((url, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5 + i * 0.15,
          duration: 0.4
        }} className="w-full rounded-2xl overflow-hidden backdrop-blur-md bg-black/40 border border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.2)]" data-id="element-27">
              <video src={url} controls className="w-full h-auto aspect-[9/16] object-cover" preload="metadata" data-id="element-28" />
            </motion.div>)}
        </div>
      </motion.div>

      {/* Org Chart / Family Tree */}
      <div className="w-full overflow-x-auto pb-10 scrollbar-thin scrollbar-thumb-purple-500/50 scrollbar-track-transparent" data-id="element-29">
        <div className="min-w-max mx-auto flex justify-center px-4" data-id="element-30">
          <TreeNode node={ORG_DATA} isRoot={true} data-id="element-31" />
        </div>
      </div>
    </div>;
}
// ── Live Schedules Page ──────────────────────────────────────
function LiveSchedules({
  onNavigate
}: {
  onNavigate: (page: PageId) => void;
}) {
  const categories = [{
    id: 'engagement',
    title: 'Engagement Lives',
    emoji: '🎯',
    agents: [{
      name: 'Baddie',
      page: 'schedule-baddie' as PageId
    }, {
      name: 'ZettaMINDset',
      page: 'schedule-zetta' as PageId
    }]
  }, {
    id: 'music',
    title: 'Music Related Lives',
    emoji: '🎵',
    agents: [{
      name: "Star's Pad Lounge",
      page: 'schedule-star-dj' as PageId
    }, {
      name: 'Nessy',
      page: 'schedule-nessy' as PageId
    }, {
      name: 'Drika',
      page: 'schedule-drika' as PageId
    }]
  }, {
    id: 'coaching',
    title: 'Coaching Lives',
    emoji: '🧠',
    agents: [{
      name: 'Trippin with Mandy - Mindset & Life Coach',
      page: 'schedule-mandy' as PageId
    }]
  }, {
    id: 'chill',
    title: 'Chill & Vibe Lives',
    emoji: '🎧',
    agents: [{
      name: "Star's Pad Lounge — Chill Days (Wed & Fri)",
      page: 'schedule-star-dj' as PageId
    }, {
      name: 'Baddie — Star Lounge After Party',
      page: 'schedule-baddie' as PageId
    }, {
      name: 'ZettaMINDset',
      page: 'schedule-zetta' as PageId
    }, {
      name: 'IAM_JDOLLA',
      page: 'schedule-jdolla' as PageId
    }, {
      name: 'J3',
      page: 'schedule-j3' as PageId
    }]
  }];
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-32">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="text-center mb-12" data-id="element-33">
        <h2 className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-34">
          Live Schedules
        </h2>
      </motion.div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-35">
        {categories.map((cat, i) => <motion.div key={cat.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4 + i * 0.1,
        duration: 0.5
      }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 md:p-8 hover:border-purple-500/30 transition-all flex flex-col" data-id="element-36">
            <h3 className="font-['Montserrat'] font-black text-2xl text-white mb-6 flex items-center gap-3" data-id="element-37">
              <span className="text-3xl" data-id="element-38">{cat.emoji}</span>
              {cat.title}
            </h3>

            {cat.agents.length > 0 ? <div className="flex flex-col gap-3 flex-1" data-id="element-39">
                {cat.agents.map((agent, idx) => <button key={idx} onClick={() => onNavigate(agent.page)} className="text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-purple-500/20 border border-white/5 hover:border-purple-500/30 transition-all text-white/80 hover:text-white font-['Montserrat'] flex items-center justify-between group" data-id="element-40">
                    <span data-id="element-41">{agent.name}</span>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-purple-400" data-id="element-42">
                      →
                    </span>
                  </button>)}
              </div> : <div className="flex-1 flex items-center justify-center text-white/30 font-['Montserrat'] italic py-8 border border-dashed border-white/10 rounded-xl" data-id="element-43">
                Coming soon...
              </div>}
          </motion.div>)}
      </div>
    </div>;
}
function AgentSchedulePage({
  agentName,
  category,
  onBack,
  bannerImage,
  tiktokUrl,
  scheduleData
}: {
  agentName: string;
  category: string;
  onBack: () => void;
  bannerImage?: string;
  tiktokUrl?: string;
  scheduleData?: {
    day: string;
    time: string;
    type: string;
    notes: string;
  }[];
}) {
  const defaultSchedule = [{
    day: 'Monday',
    time: '6:00 PM EST',
    type: 'Battle Live',
    notes: 'Weekly battle stream'
  }, {
    day: 'Tuesday',
    time: '7:00 PM EST',
    type: 'Engagement Live',
    notes: 'Community engagement & chat'
  }, {
    day: 'Wednesday',
    time: '6:00 PM EST',
    type: 'Themed Live',
    notes: 'Special themed content'
  }, {
    day: 'Thursday',
    time: '7:00 PM EST',
    type: 'Collab Live',
    notes: 'Guest collaborations'
  }, {
    day: 'Friday',
    time: '8:00 PM EST',
    type: 'Music / Vibe Live',
    notes: 'Music & chill vibes'
  }, {
    day: 'Saturday',
    time: '3:00 PM EST',
    type: 'Open Live',
    notes: 'Open format — anything goes'
  }, {
    day: 'Sunday',
    time: 'OFF',
    type: 'Rest Day',
    notes: 'Recharge & prep for next week'
  }];
  const schedule = scheduleData || defaultSchedule;
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-44">
      <div className="max-w-4xl mx-auto" data-id="element-45">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10" data-id="element-46">
          <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm mb-6 flex items-center gap-2" data-id="element-47">
            ← Back to Live Schedules
          </button>

          {bannerImage && <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="mb-8" data-id="element-48">
              {tiktokUrl ? <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="block transition-transform hover:scale-[1.01] max-w-sm mx-auto" data-id="element-49">
                  <img src={bannerImage} alt={`${agentName} Banner`} className="w-full h-auto rounded-2xl border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-50" />
                </a> : <div className="max-w-sm mx-auto" data-id="element-51">
                  <img src={bannerImage} alt={`${agentName} Banner`} className="w-full h-auto rounded-2xl border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-52" />
                </div>}
            </motion.div>}

          {!bannerImage && <>
              <h2 className="font-['Montserrat'] font-black text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-2" data-id="element-53">
                {agentName}
              </h2>
              <p className="text-white/50 font-['Montserrat'] text-lg uppercase tracking-wider" data-id="element-54">
                {category}
              </p>
            </>}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl overflow-hidden" data-id="element-55">
          <div className="p-6 bg-white/5 border-b border-white/10" data-id="element-56">
            <h3 className="font-['Montserrat'] font-bold text-2xl text-white flex items-center gap-3" data-id="element-57">
              🗓️ {agentName}'s Weekly Schedule
            </h3>
          </div>
          <div className="divide-y divide-white/5" data-id="element-58">
            {schedule.map((item, i) => <motion.div key={item.day} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3 + i * 0.05
          }} className="flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-colors" data-id="element-59">
                <div className="w-28 flex-shrink-0" data-id="element-60">
                  <span className="font-['Montserrat'] font-bold text-white text-sm" data-id="element-61">
                    {item.day}
                  </span>
                </div>
                <div className="w-28 flex-shrink-0" data-id="element-62">
                  <span className={`font-['Montserrat'] text-sm ${item.time === 'OFF' ? 'text-white/30 italic' : 'text-purple-300 font-semibold'}`} data-id="element-63">
                    {item.time}
                  </span>
                </div>
                <div className="flex-1" data-id="element-64">
                  <span className="font-['Montserrat'] text-white/80 text-sm font-medium" data-id="element-65">
                    {item.type}
                  </span>
                  <span className="text-white/40 text-xs ml-2" data-id="element-66">
                    — {item.notes}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}
// ── WeeklySchedules ──────────────────────────────────────────────
function WeeklySchedules() {
  const days = [{
    day: 'Monday',
    schedule: '10:00 AM - Team Sync\n2:00 PM - Creator Reviews'
  }, {
    day: 'Tuesday',
    schedule: '11:00 AM - QVC Strategy\n3:00 PM - Open Office Hours'
  }, {
    day: 'Wednesday',
    schedule: '1:00 PM - Mid-week Check-in\n4:00 PM - Content Planning'
  }, {
    day: 'Thursday',
    schedule: '10:30 AM - Live Stream Coaching\n2:30 PM - Growth Workshop'
  }, {
    day: 'Friday',
    schedule: '12:00 PM - Weekly Wrap-up\n3:00 PM - Community Highlights'
  }, {
    day: 'Saturday',
    schedule: 'Weekend Live Events'
  }, {
    day: 'Sunday',
    schedule: 'Rest & Prep for next week'
  }];
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-67">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="text-center mb-12" data-id="element-68">
        <h2 className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-69">
          Weekly Schedules
        </h2>
        <p className="text-white/50 text-lg font-['Montserrat'] max-w-2xl mx-auto" data-id="element-70">
          Stay up to date with our weekly events, coaching sessions, and team
          syncs.
        </p>
      </motion.div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-71">
        {days.map((d, i) => <motion.div key={d.day} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2 + i * 0.1,
        duration: 0.5
      }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-6 md:p-8 hover:border-purple-500/30 transition-all" data-id="element-72">
            <h3 className="font-['Montserrat'] font-black text-2xl text-white mb-4 flex items-center gap-3" data-id="element-73">
              <span className="w-2 h-8 rounded-full bg-gradient-to-b from-purple-500 to-cyan-500" data-id="element-74" />
              {d.day}
            </h3>
            <p className="text-white/70 font-['Montserrat'] whitespace-pre-wrap leading-relaxed" data-id="element-75">
              {d.schedule}
            </p>
          </motion.div>)}
      </div>
    </div>;
}
// ── Navbar ──────────────────────────────────────────────────────
function Navbar({
  currentPage,
  onNavigate
}: {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4" data-id="element-76">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-md border-b border-white/10" data-id="element-77" />

      <div className="relative max-w-7xl mx-auto flex items-center justify-between" data-id="element-78">
        {/* Logo */}
        <button onClick={() => onNavigate('home')} className="flex items-center gap-2 group" data-id="element-79">
          <span className="font-['Montserrat'] font-black text-2xl tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-300 group-hover:to-blue-300 transition-all duration-300" data-id="element-80">
            GSF
          </span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8" data-id="element-81">
          {NAV_ITEMS.map(item => <button key={item.label} onClick={() => onNavigate(item.page)} className={`relative font-['Inter'] text-sm font-medium transition-colors py-2 group ${currentPage === item.page ? 'text-white' : 'text-white/80 hover:text-white'}`} data-id="element-82">
              {item.label}
              <span className={`absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-purple-500 to-blue-500 transition-transform duration-300 origin-left shadow-[0_0_8px_rgba(168,85,247,0.6)] ${currentPage === item.page ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-83" />
            </button>)}
          <div className="h-6 w-[1px] bg-white/20 mx-2" data-id="element-84" />
          <div className="flex items-center gap-4" data-id="element-85">
            <button onClick={() => onNavigate('agent-login')} className="text-xs font-['Montserrat'] font-semibold text-white/50 hover:text-purple-300 transition-colors" data-id="element-86">
              Agent Login
            </button>
            <button onClick={() => onNavigate('creator-login')} className="text-xs font-['Montserrat'] font-semibold text-white/50 hover:text-cyan-300 transition-colors" data-id="element-87">
              Creator Login
            </button>
          </div>
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setIsOpen(!isOpen)} className="md:hidden relative z-50 p-2 text-white hover:text-white transition-colors" data-id="element-88">
          {isOpen ? <XIcon size={24} data-id="element-89" /> : <MenuIcon size={24} data-id="element-90" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-91">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.2
      }} className="absolute top-full left-0 right-0 bg-black/80 backdrop-blur-xl border-b border-white/10 md:hidden" data-id="element-92">
            <div className="flex flex-col px-6 py-4 gap-4" data-id="element-93">
              {NAV_ITEMS.map(item => <button key={item.label} onClick={() => {
            onNavigate(item.page);
            setIsOpen(false);
          }} className={`text-left font-['Inter'] text-lg font-medium hover:pl-2 transition-all duration-300 border-l-2 ${currentPage === item.page ? 'text-white border-purple-500 pl-2' : 'text-white hover:text-white'}`} data-id="element-94">
                  {item.label}
                </button>)}
              <div className="w-full h-[1px] bg-white/10 my-2" data-id="element-95" />
              <button onClick={() => {
            onNavigate('agent-login');
            setIsOpen(false);
          }} className="text-left font-['Montserrat'] text-sm font-semibold text-white/50 hover:text-purple-300 transition-colors" data-id="element-96">
                Agent Login
              </button>
              <button onClick={() => {
            onNavigate('creator-login');
            setIsOpen(false);
          }} className="text-left font-['Montserrat'] text-sm font-semibold text-white/50 hover:text-cyan-300 transition-colors" data-id="element-97">
                Creator Login
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}
// ── LoginPage ──────────────────────────────────────────────────
function LoginPage({
  title,
  type,
  onLogin
}: {
  title: string;
  type: 'agent' | 'creator';
  onLogin: (displayName: string, role: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      let cred;
      if (type === 'agent') {
        cred = await signInWithEmailAndPassword(auth, email, password);
      } else {
        cred = await signInWithEmailAndPassword(creatorAuth, email, password);
      }
      let displayName = email.split('@')[0];
      let userRole = 'creator';
      try {
        const uid = cred.user.uid;
        const userDocSnap = await getDoc(doc(db, 'users', uid));
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          displayName = userData?.Name || userData?.name || displayName;
          userRole = userData?.role || 'creator';
        }
      } catch (e) {
        // Fallback
      }
      if (type === 'agent' && userRole === 'creator') {
        await signOut(auth);
        setError('Creator accounts cannot access the Agent portal. Please use the Creator Login instead.');
        return;
      }
      onLogin(displayName, userRole);
    } catch (err: any) {
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-20" data-id="element-98">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="max-w-md w-full backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-8 shadow-[0_0_40px_rgba(168,85,247,0.15)]" data-id="element-99">
        <div className="text-center mb-8" data-id="element-100">
          <h2 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-cyan-300 mb-2" data-id="element-101">
            {title}
          </h2>
          <p className="text-white/50 text-sm font-['Montserrat']" data-id="element-102">
            Enter your credentials to access the portal
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-103">
          {error && <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 text-sm text-center" data-id="element-104">
              {error}
            </div>}
          <div data-id="element-105">
            <label className="block text-white/70 text-sm font-medium mb-2 font-['Montserrat']" data-id="element-106">
              Email
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-['Inter']" placeholder="Enter your email" required data-id="element-107" />
          </div>
          <div data-id="element-108">
            <label className="block text-white/70 text-sm font-medium mb-2 font-['Montserrat']" data-id="element-109">
              Password
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-['Inter']" placeholder="••••••••" required data-id="element-110" />
          </div>
          <button type="submit" disabled={loading} className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-base font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-111">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </motion.div>
    </div>;
}
// ── Portal Interfaces & Components ────────────────────────────────
interface PostLike {
  email: string;
  name: string;
}
interface Post {
  id: string;
  content: string;
  imageUrl?: string;
  authorName: string;
  authorInitials: string;
  createdAt: Timestamp | null;
  sharedToCreators?: boolean;
  postedBy?: 'agent' | 'creator';
  crossPosted?: boolean;
  fontColor?: string;
  fontSize?: string;
  pinned?: boolean;
  likes?: PostLike[];
  comments: {
    content: string;
    authorName: string;
    authorInitials: string;
    createdAt: Timestamp | null;
  }[];
}
interface MeetingNote {
  notes: string;
  title: string;
  updatedAt: Timestamp | null;
  updatedBy: string;
}
function PortalPage({
  user,
  displayName,
  userRole,
  title,
  subtitle,
  collectionName,
  notesCollectionName,
  onSignOut,
  onBack
}: {
  user: User | null;
  displayName: string;
  userRole: string;
  title: string;
  subtitle: string;
  collectionName: string;
  notesCollectionName: string;
  onSignOut: () => void;
  onBack: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [meetingNotesText, setMeetingNotesText] = useState('');
  const [meetingNotesDocId, setMeetingNotesDocId] = useState('');
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Timestamp | null>(null);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [commentInputs, setCommentInputs] = useState<{
    [postId: string]: string;
  }>({});
  const [isPosting, setIsPosting] = useState(false);
  const [shareToCreators, setShareToCreators] = useState(false);
  const [fontColor, setFontColor] = useState('#ffffff');
  const [fontSize, setFontSize] = useState('base');
  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const postsData: Post[] = [];
      snapshot.forEach(doc => {
        postsData.push({
          id: doc.id,
          ...doc.data()
        } as Post);
      });
      postsData.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return 0;
      });
      setPosts(postsData);
    });
    const notesDocRef = doc(db, 'site_content', notesCollectionName);
    const notesUnsubscribe = onSnapshot(notesDocRef, docSnap => {
      if (docSnap.exists()) {
        const data = docSnap.data() as MeetingNote;
        setMeetingNotesDocId(docSnap.id);
        setMeetingNotesText(data.notes || '');
        setLastUpdatedBy(data.updatedBy || '');
        setLastUpdatedAt(data.updatedAt);
      } else {
        setMeetingNotesDocId('');
        setMeetingNotesText('');
        setLastUpdatedBy('');
        setLastUpdatedAt(null);
      }
    });
    return () => {
      unsubscribe();
      notesUnsubscribe();
    };
  }, [collectionName, notesCollectionName]);
  const handlePost = async () => {
    if (!newPostContent.trim() || !user) return;
    setIsPosting(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const initials = name.substring(0, 2).toUpperCase();
      await addDoc(collection(db, collectionName), {
        content: newPostContent,
        authorName: name,
        authorInitials: initials,
        createdAt: serverTimestamp(),
        comments: [],
        postedBy: 'agent',
        crossPosted: shareToCreators,
        fontColor,
        fontSize
      });
      setNewPostContent('');
      setShareToCreators(false);
      setFontColor('#ffffff');
      setFontSize('base');
    } catch (error: any) {
      console.error('Error adding post:', error);
      alert('Failed to post: ' + (error?.message || 'Unknown error.'));
    } finally {
      setIsPosting(false);
    }
  };
  const handleSaveMeetingNotes = async () => {
    if (!user) return;
    setIsSavingNotes(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const notesDocRef = doc(db, 'site_content', notesCollectionName);
      await setDoc(notesDocRef, {
        notes: meetingNotesText,
        title: 'Weekly Meeting Notes',
        updatedAt: serverTimestamp(),
        updatedBy: name
      }, {
        merge: true
      });
      setMeetingNotesDocId(notesCollectionName);
    } catch (error) {
      console.error('Error saving meeting notes:', error);
      alert('Failed to save notes.');
    } finally {
      setIsSavingNotes(false);
    }
  };
  const handleAddMeetingNote = async () => {
    if (!newMeetingNote.trim() || !user) return;
    try {
      await addDoc(collection(db, notesCollectionName), {
        content: newMeetingNote,
        date: new Date().toLocaleDateString(),
        createdAt: serverTimestamp()
      });
      setNewMeetingNote('');
    } catch (error) {
      console.error('Error adding meeting note:', error);
    }
  };
  const handlePinPost = async (postId: string, currentPinned: boolean) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, collectionName, postId), {
        pinned: !currentPinned
      });
    } catch (error) {
      console.error('Error pinning post:', error);
    }
  };
  const handleLike = async (post: Post) => {
    if (!user) return;
    const email = user.email || '';
    const name = displayName || email.split('@')[0] || 'Agent';
    const alreadyLiked = post.likes?.some(l => l.email === email);
    const postRef = doc(db, collectionName, post.id);
    try {
      if (alreadyLiked) {
        const updatedLikes = (post.likes || []).filter(l => l.email !== email);
        await updateDoc(postRef, {
          likes: updatedLikes
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion({
            email,
            name
          })
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  const handleDeletePost = async (postId: string) => {
    if (!user) return;
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await deleteDoc(doc(db, collectionName, postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };
  const handleComment = async (postId: string) => {
    const content = commentInputs[postId];
    if (!content?.trim() || !user) return;
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const initials = name.substring(0, 2).toUpperCase();
      const postRef = doc(db, collectionName, postId);
      await updateDoc(postRef, {
        comments: arrayUnion({
          content,
          authorName: name,
          authorInitials: initials,
          createdAt: Timestamp.now()
        })
      });
      setCommentInputs(prev => ({
        ...prev,
        [postId]: ''
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  const handleDeleteComment = async (postId: string, commentToDelete: {
    content: string;
    authorName: string;
    authorInitials: string;
    createdAt: Timestamp | null;
  }) => {
    if (!user) return;
    const currentName = displayName || user.email?.split('@')[0] || 'Agent';
    if (commentToDelete.authorName !== currentName) return;
    try {
      const postRef = doc(db, collectionName, postId);
      const postSnap = await getDoc(postRef);
      if (postSnap.exists()) {
        const data = postSnap.data();
        const updatedComments = (data.comments || []).filter((c: any) => !(c.content === commentToDelete.content && c.authorName === commentToDelete.authorName && c.createdAt?.seconds === commentToDelete.createdAt?.seconds));
        await updateDoc(postRef, {
          comments: updatedComments
        });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-112">
      <div className="max-w-6xl mx-auto" data-id="element-113">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10 flex items-start justify-between" data-id="element-114">
          <div data-id="element-115">
            <div className="flex items-center gap-4 mb-2" data-id="element-116">
              <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm" data-id="element-117">
                ← Back
              </button>
              <h2 className="font-['Montserrat'] font-black text-3xl md:text-4xl text-white" data-id="element-118">
                {title}
              </h2>
            </div>
            <p className="text-white/50 font-['Montserrat']" data-id="element-119">{subtitle}</p>
          </div>
          {user && <button onClick={onSignOut} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white text-sm font-['Montserrat'] hover:border-red-500/30 hover:text-red-300 transition-all" data-id="element-120">
              <LogOutIcon size={16} data-id="element-121" />
              Sign Out
            </button>}
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-6" data-id="element-122">
          {/* Left Column: Chat Feed */}
          <div className="flex-1 min-w-0" data-id="element-123">
            {user && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 mb-8" data-id="element-124">
                <textarea value={newPostContent} onChange={e => setNewPostContent(e.target.value)} style={{
              color: fontColor
            }} className={`w-full bg-black/20 border border-white/5 rounded-xl p-4 placeholder-white/40 focus:outline-none focus:border-purple-500/50 resize-none font-['Inter'] ${fontSize === 'sm' ? 'text-sm' : fontSize === 'lg' ? 'text-lg' : fontSize === 'xl' ? 'text-xl' : fontSize === '2xl' ? 'text-2xl' : 'text-base'}`} rows={3} placeholder="Share an update with the team..." data-id="element-125" />
                <div className="flex flex-wrap items-center gap-3 mt-4" data-id="element-126">
                  <div className="flex items-center gap-2" data-id="element-127">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-128">
                      Color:
                    </span>
                    <div className="flex gap-1.5" data-id="element-129">
                      {[{
                    color: '#ffffff',
                    label: 'White'
                  }, {
                    color: '#c084fc',
                    label: 'Purple'
                  }, {
                    color: '#22d3ee',
                    label: 'Cyan'
                  }, {
                    color: '#f472b6',
                    label: 'Pink'
                  }, {
                    color: '#facc15',
                    label: 'Yellow'
                  }, {
                    color: '#4ade80',
                    label: 'Green'
                  }, {
                    color: '#f87171',
                    label: 'Red'
                  }].map(c => <button key={c.color} type="button" onClick={() => setFontColor(c.color)} title={c.label} className={`w-6 h-6 rounded-full border-2 transition-all ${fontColor === c.color ? 'border-white scale-110' : 'border-white/20 hover:border-white/50'}`} style={{
                    backgroundColor: c.color
                  }} data-id="element-130" />)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-131">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-132">
                      Size:
                    </span>
                    <div className="flex gap-1" data-id="element-133">
                      {[{
                    value: 'sm',
                    label: 'S'
                  }, {
                    value: 'base',
                    label: 'M'
                  }, {
                    value: 'lg',
                    label: 'L'
                  }, {
                    value: 'xl',
                    label: 'XL'
                  }, {
                    value: '2xl',
                    label: '2XL'
                  }].map(s => <button key={s.value} type="button" onClick={() => setFontSize(s.value)} className={`px-2 py-0.5 rounded text-xs font-['Montserrat'] font-bold transition-all ${fontSize === s.value ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300' : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-134">
                          {s.label}
                        </button>)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3" data-id="element-135">
                  <button type="button" onClick={() => setShareToCreators(!shareToCreators)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all font-['Montserrat'] text-sm ${shareToCreators ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-136">
                    <span className="text-base" data-id="element-137">
                      {shareToCreators ? '✅' : '⬜'}
                    </span>
                    Cross-post to Creators
                  </button>
                  <button onClick={handlePost} disabled={isPosting || !newPostContent.trim()} className="rounded-lg bg-purple-600/80 hover:bg-purple-500 text-white font-semibold text-sm px-4 py-2" data-id="element-138">
                    {isPosting ? 'Posting...' : 'Post Update'}
                  </button>
                </div>
              </motion.div>}

            {!user && <div className="mb-8 p-4 rounded-xl bg-purple-500/20 border border-purple-500/30 text-purple-200 text-center font-['Montserrat']" data-id="element-139">
                You are viewing the portal in read-only mode. Please login as an
                Agent to post and comment.
              </div>}

            {/* Mobile Meeting Notes */}
            <div className="lg:hidden mb-8 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-140">
              <button onClick={() => setIsNotesExpanded(!isNotesExpanded)} className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors" data-id="element-141">
                <h3 className="font-['Montserrat'] font-bold text-xl text-white flex items-center gap-2" data-id="element-142">
                  📝 Weekly Meeting Notes
                </h3>
                <span className="text-white/50" data-id="element-143">
                  {isNotesExpanded ? '▲' : '▼'}
                </span>
              </button>
              <AnimatePresence data-id="element-144">
                {isNotesExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="border-t border-white/10" data-id="element-145">
                    <div className="p-4 space-y-4" data-id="element-146">
                      {user ? <div className="flex flex-col gap-3" data-id="element-147">
                          <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[120px] font-['Inter']" data-id="element-148" />
                          <div className="flex items-center justify-between" data-id="element-149">
                            <div className="text-white/40 text-xs font-['Montserrat']" data-id="element-150">
                              {lastUpdatedBy && <>
                                  Last updated by {lastUpdatedBy}
                                  {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                                </>}
                            </div>
                            <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-151">
                              {isSavingNotes ? 'Saving...' : 'Save Notes'}
                            </button>
                          </div>
                        </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-152">
                          <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-153">
                            {meetingNotesText || <span className="italic text-white/40" data-id="element-154">
                                No meeting notes available.
                              </span>}
                          </p>
                          {lastUpdatedBy && <div className="mt-3 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-155">
                              Last updated by {lastUpdatedBy}
                              {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                            </div>}
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Feed */}
            <div className="space-y-6" data-id="element-156">
              {posts.length === 0 ? <div className="text-center py-10 text-white/40 font-['Montserrat']" data-id="element-157">
                  No posts yet. Be the first to share an update!
                </div> : posts.map((post, idx) => <motion.div key={post.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + idx * 0.1
            }} className={`backdrop-blur-md rounded-2xl p-5 md:p-6 ${post.pinned ? 'bg-black/40 border border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.2)]' : post.crossPosted ? 'bg-cyan-900/20 border border-cyan-500/20 shadow-[0_0_15px_rgba(34,211,238,0.1)]' : 'bg-black/40 border border-white/5'}`} data-id="element-158">
                    {post.pinned && <div className="flex items-center gap-2 text-purple-400 text-xs font-bold font-['Montserrat'] mb-3 uppercase tracking-wider" data-id="element-159">
                        📌 Pinned Post
                      </div>}
                    <div className="flex items-center gap-3 mb-4" data-id="element-160">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm" data-id="element-161">
                        {post.authorInitials}
                      </div>
                      <div className="flex-1" data-id="element-162">
                        <div className="flex items-center gap-2" data-id="element-163">
                          <h4 className="text-white font-semibold font-['Montserrat']" data-id="element-164">
                            {post.authorName}
                          </h4>
                          {post.crossPosted && <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-[10px] font-semibold font-['Montserrat']" data-id="element-165">
                              Shared to Creators
                            </span>}
                        </div>
                        <p className="text-white/40 text-xs" data-id="element-166">
                          {post.createdAt ? safeToDate(post.createdAt)?.toLocaleString() ?? 'Just now' : 'Just now'}
                        </p>
                      </div>
                      {user && <div className="flex items-center gap-2" data-id="element-167">
                          <button onClick={() => handlePinPost(post.id, !!post.pinned)} className={`transition-colors text-sm font-['Montserrat'] p-1 ${post.pinned ? 'text-purple-400 hover:text-purple-300' : 'text-white/20 hover:text-purple-400'}`} title={post.pinned ? 'Unpin post' : 'Pin post'} data-id="element-168">
                            📌
                          </button>
                          <button onClick={() => handleDeletePost(post.id)} className="text-white/20 hover:text-red-400 transition-colors text-sm font-['Montserrat'] p-1" title="Delete post" data-id="element-169">
                            🗑️
                          </button>
                        </div>}
                    </div>

                    {post.content && <p className={`leading-relaxed mb-4 whitespace-pre-wrap ${post.fontSize === 'sm' ? 'text-sm' : post.fontSize === 'lg' ? 'text-lg' : post.fontSize === 'xl' ? 'text-xl' : post.fontSize === '2xl' ? 'text-2xl' : 'text-sm md:text-base'}`} style={{
                color: post.fontColor || 'rgba(255,255,255,0.8)'
              }} data-id="element-170">
                        {post.content}
                      </p>}

                    <div className="mb-4 flex items-center gap-3" data-id="element-171">
                      <button onClick={() => user && handleLike(post)} className={`flex items-center gap-1.5 text-sm font-['Montserrat'] transition-all ${!user ? 'text-white/30 cursor-default' : post.likes?.some(l => l.email === user.email) ? 'text-pink-400' : 'text-white/40 hover:text-pink-400'}`} data-id="element-172">
                        <span className="text-lg" data-id="element-173">
                          {user && post.likes?.some(l => l.email === user.email) ? '❤️' : '🤍'}
                        </span>
                        <span data-id="element-174">{post.likes?.length || 0}</span>
                      </button>
                      {(post.likes?.length || 0) > 0 && <p className="text-white/30 text-xs font-['Montserrat']" data-id="element-175">
                          Liked by {post.likes?.map(l => l.name).join(', ')}
                        </p>}
                    </div>

                    <div className="border-t border-white/10 pt-4 space-y-4" data-id="element-176">
                      {post.comments?.map((comment, cIdx) => <div key={cIdx} className="flex gap-3" data-id="element-177">
                          <div className="w-8 h-8 rounded-full bg-pink-500/50 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" data-id="element-178">
                            {comment.authorInitials}
                          </div>
                          <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 flex-1" data-id="element-179">
                            <div className="flex justify-between items-baseline mb-1" data-id="element-180">
                              <h5 className="text-white/90 text-sm font-semibold" data-id="element-181">
                                {comment.authorName}
                              </h5>
                              <div className="flex items-center gap-2" data-id="element-182">
                                <span className="text-white/30 text-[10px]" data-id="element-183">
                                  {comment.createdAt ? safeToDate(comment.createdAt)?.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) ?? '' : ''}
                                </span>
                                {user && comment.authorName === (displayName || user.email?.split('@')[0] || '') && <button onClick={() => handleDeleteComment(post.id, comment)} className="text-white/20 hover:text-red-400 transition-colors text-xs" title="Delete comment" data-id="element-184">
                                      ✕
                                    </button>}
                              </div>
                            </div>
                            <p className="text-white/70 text-sm whitespace-pre-wrap" data-id="element-185">
                              {comment.content}
                            </p>
                          </div>
                        </div>)}

                      {user && <div className="flex gap-3 items-center mt-4" data-id="element-186">
                          <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-white/50 text-xs font-bold" data-id="element-187">
                            {displayName ? displayName.substring(0, 2).toUpperCase() : user.email?.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 flex gap-2" data-id="element-188">
                            <input type="text" value={commentInputs[post.id] || ''} onChange={e => setCommentInputs(prev => ({
                      ...prev,
                      [post.id]: e.target.value
                    }))} onKeyDown={e => e.key === 'Enter' && handleComment(post.id)} placeholder="Add a comment..." className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none placeholder-white/30" data-id="element-189" />
                            <button onClick={() => handleComment(post.id)} disabled={!commentInputs[post.id]?.trim()} className="text-purple-400 hover:text-purple-300" data-id="element-190">
                              Post
                            </button>
                          </div>
                        </div>}
                    </div>
                  </motion.div>)}
            </div>
          </div>

          {/* Right Column: Meeting Notes Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0" data-id="element-191">
            <div className="sticky top-28 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-192">
              <div className="p-4 bg-white/5 border-b border-white/10" data-id="element-193">
                <h3 className="font-['Montserrat'] font-bold text-lg text-white flex items-center gap-2" data-id="element-194">
                  📝 Weekly Meeting Notes
                </h3>
              </div>
              <div className="p-4 space-y-4" data-id="element-195">
                {user ? <div className="flex flex-col gap-3" data-id="element-196">
                    <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[200px] font-['Inter']" data-id="element-197" />
                    <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="w-full px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-198">
                      {isSavingNotes ? 'Saving...' : 'Save Notes'}
                    </button>
                    {lastUpdatedBy && <div className="text-white/40 text-xs font-['Montserrat'] text-center mt-2" data-id="element-199">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-200" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-201">
                    <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-202">
                      {meetingNotesText || <span className="italic text-white/40" data-id="element-203">
                          No meeting notes available.
                        </span>}
                    </p>
                    {lastUpdatedBy && <div className="mt-4 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-204">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-205" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ── CreatorPortalPage ───────────────────────────────────────────
function CreatorPortalPage({
  user,
  displayName,
  userRole,
  title,
  subtitle,
  collectionName,
  notesCollectionName,
  onSignOut,
  onBack
}: {
  user: User | null;
  displayName: string;
  userRole: string;
  title: string;
  subtitle: string;
  collectionName: string;
  notesCollectionName: string;
  onSignOut: () => void;
  onBack: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [meetingNotesText, setMeetingNotesText] = useState('');
  const [meetingNotesDocId, setMeetingNotesDocId] = useState('');
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Timestamp | null>(null);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [commentInputs, setCommentInputs] = useState<{
    [postId: string]: string;
  }>({});
  const [isPosting, setIsPosting] = useState(false);
  const [fontColor, setFontColor] = useState('#ffffff');
  const [fontSize, setFontSize] = useState('base');
  const [newMeetingNote, setNewMeetingNote] = useState('');
  const canPost = userRole === 'agent' || userRole === 'admin';
  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const postsData: Post[] = [];
      snapshot.forEach(doc => {
        postsData.push({
          id: doc.id,
          ...doc.data()
        } as Post);
      });
      const filtered = userRole === 'agent' || userRole === 'admin' ? postsData : postsData.filter(p => p.postedBy === 'creator' || p.crossPosted === true);
      filtered.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return 0;
      });
      setPosts(filtered);
    });
    const notesDocRef = doc(db, 'site_content', notesCollectionName);
    const notesUnsubscribe = onSnapshot(notesDocRef, docSnap => {
      if (docSnap.exists()) {
        const data = docSnap.data() as MeetingNote;
        setMeetingNotesDocId(docSnap.id);
        setMeetingNotesText(data.notes || '');
        setLastUpdatedBy(data.updatedBy || '');
        setLastUpdatedAt(data.updatedAt);
      } else {
        setMeetingNotesDocId('');
        setMeetingNotesText('');
        setLastUpdatedBy('');
        setLastUpdatedAt(null);
      }
    });
    return () => {
      unsubscribe();
      notesUnsubscribe();
    };
  }, [collectionName, notesCollectionName, userRole]);
  const handlePost = async () => {
    if (!newPostContent.trim() || !user || !canPost) return;
    setIsPosting(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Creator';
      const initials = name.substring(0, 2).toUpperCase();
      await addDoc(collection(db, collectionName), {
        content: newPostContent,
        authorName: name,
        authorInitials: initials,
        createdAt: serverTimestamp(),
        comments: [],
        postedBy: 'creator',
        fontColor,
        fontSize
      });
      setNewPostContent('');
      setFontColor('#ffffff');
      setFontSize('base');
    } catch (error: any) {
      console.error('Error adding post:', error);
      alert('Failed to post: ' + (error?.message || 'Unknown error.'));
    } finally {
      setIsPosting(false);
    }
  };
  const handleSaveMeetingNotes = async () => {
    if (!user || !canPost) return;
    setIsSavingNotes(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const notesDocRef = doc(db, 'site_content', notesCollectionName);
      await setDoc(notesDocRef, {
        notes: meetingNotesText,
        title: 'Weekly Meeting Notes',
        updatedAt: serverTimestamp(),
        updatedBy: name
      }, {
        merge: true
      });
      setMeetingNotesDocId(notesCollectionName);
    } catch (error) {
      console.error('Error saving meeting notes:', error);
      alert('Failed to save notes.');
    } finally {
      setIsSavingNotes(false);
    }
  };
  const handleLike = async (post: Post) => {
    if (!user) return;
    const email = user.email || '';
    const name = displayName || email.split('@')[0] || 'Creator';
    const alreadyLiked = post.likes?.some(l => l.email === email);
    const postRef = doc(db, collectionName, post.id);
    try {
      if (alreadyLiked) {
        const updatedLikes = (post.likes || []).filter(l => l.email !== email);
        await updateDoc(postRef, {
          likes: updatedLikes
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion({
            email,
            name
          })
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  const handleComment = async (postId: string) => {
    const content = commentInputs[postId];
    if (!content?.trim() || !user) return;
    try {
      const name = displayName || user.email?.split('@')[0] || 'Creator';
      const initials = name.substring(0, 2).toUpperCase();
      const postRef = doc(db, collectionName, postId);
      await updateDoc(postRef, {
        comments: arrayUnion({
          content,
          authorName: name,
          authorInitials: initials,
          createdAt: Timestamp.now()
        })
      });
      setCommentInputs(prev => ({
        ...prev,
        [postId]: ''
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  const handleDeleteComment = async (postId: string, commentToDelete: {
    content: string;
    authorName: string;
    authorInitials: string;
    createdAt: Timestamp | null;
  }) => {
    if (!user) return;
    const currentName = displayName || user.email?.split('@')[0] || 'Creator';
    if (commentToDelete.authorName !== currentName) return;
    try {
      const postRef = doc(db, collectionName, postId);
      const postSnap = await getDoc(postRef);
      if (postSnap.exists()) {
        const data = postSnap.data();
        const updatedComments = (data.comments || []).filter((c: any) => !(c.content === commentToDelete.content && c.authorName === commentToDelete.authorName && c.createdAt?.seconds === commentToDelete.createdAt?.seconds));
        await updateDoc(postRef, {
          comments: updatedComments
        });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-206">
      <div className="max-w-6xl mx-auto" data-id="element-207">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10 flex items-start justify-between" data-id="element-208">
          <div data-id="element-209">
            <div className="flex items-center gap-4 mb-2" data-id="element-210">
              <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm" data-id="element-211">
                ← Back
              </button>
              <h2 className="font-['Montserrat'] font-black text-3xl md:text-4xl text-white" data-id="element-212">
                {title}
              </h2>
            </div>
            <p className="text-white/50 font-['Montserrat']" data-id="element-213">{subtitle}</p>
          </div>
          {user && <button onClick={onSignOut} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white text-sm font-['Montserrat'] hover:border-red-500/30 hover:text-red-300 transition-all" data-id="element-214">
              <LogOutIcon size={16} data-id="element-215" />
              Sign Out
            </button>}
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-6" data-id="element-216">
          {/* Left Column: Chat Feed */}
          <div className="flex-1 min-w-0" data-id="element-217">
            {user && canPost && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 mb-8" data-id="element-218">
                <textarea value={newPostContent} onChange={e => setNewPostContent(e.target.value)} style={{
              color: fontColor
            }} className={`w-full bg-black/20 border border-white/5 rounded-xl p-4 placeholder-white/40 focus:outline-none focus:border-purple-500/50 resize-none font-['Inter'] ${fontSize === 'sm' ? 'text-sm' : fontSize === 'lg' ? 'text-lg' : fontSize === 'xl' ? 'text-xl' : fontSize === '2xl' ? 'text-2xl' : 'text-base'}`} rows={3} placeholder="Share an update with the creators..." data-id="element-219" />
                <div className="flex flex-wrap items-center gap-3 mt-4" data-id="element-220">
                  <div className="flex items-center gap-2" data-id="element-221">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-222">
                      Color:
                    </span>
                    <div className="flex gap-1.5" data-id="element-223">
                      {[{
                    color: '#ffffff',
                    label: 'White'
                  }, {
                    color: '#c084fc',
                    label: 'Purple'
                  }, {
                    color: '#22d3ee',
                    label: 'Cyan'
                  }, {
                    color: '#f472b6',
                    label: 'Pink'
                  }, {
                    color: '#facc15',
                    label: 'Yellow'
                  }, {
                    color: '#4ade80',
                    label: 'Green'
                  }, {
                    color: '#f87171',
                    label: 'Red'
                  }].map(c => <button key={c.color} type="button" onClick={() => setFontColor(c.color)} title={c.label} className={`w-6 h-6 rounded-full border-2 transition-all ${fontColor === c.color ? 'border-white scale-110' : 'border-white/20 hover:border-white/50'}`} style={{
                    backgroundColor: c.color
                  }} data-id="element-224" />)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-225">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-226">
                      Size:
                    </span>
                    <div className="flex gap-1" data-id="element-227">
                      {[{
                    value: 'sm',
                    label: 'S'
                  }, {
                    value: 'base',
                    label: 'M'
                  }, {
                    value: 'lg',
                    label: 'L'
                  }, {
                    value: 'xl',
                    label: 'XL'
                  }, {
                    value: '2xl',
                    label: '2XL'
                  }].map(s => <button key={s.value} type="button" onClick={() => setFontSize(s.value)} className={`px-2 py-0.5 rounded text-xs font-['Montserrat'] font-bold transition-all ${fontSize === s.value ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300' : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-228">
                          {s.label}
                        </button>)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-3" data-id="element-229">
                  <button onClick={handlePost} disabled={isPosting || !newPostContent.trim()} className="rounded-lg bg-purple-600/80 hover:bg-purple-500 text-white font-semibold text-sm px-4 py-2" data-id="element-230">
                    {isPosting ? 'Posting...' : 'Post Update'}
                  </button>
                </div>
              </motion.div>}

            {/* Mobile Meeting Notes */}
            <div className="lg:hidden mb-8 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-231">
              <button onClick={() => setIsNotesExpanded(!isNotesExpanded)} className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors" data-id="element-232">
                <h3 className="font-['Montserrat'] font-bold text-xl text-white flex items-center gap-2" data-id="element-233">
                  📝 Weekly Meeting Notes
                </h3>
                <span className="text-white/50" data-id="element-234">
                  {isNotesExpanded ? '▲' : '▼'}
                </span>
              </button>
              <AnimatePresence data-id="element-235">
                {isNotesExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="border-t border-white/10" data-id="element-236">
                    <div className="p-4 space-y-4" data-id="element-237">
                      {user && canPost ? <div className="flex flex-col gap-3" data-id="element-238">
                          <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[120px] font-['Inter']" data-id="element-239" />
                          <div className="flex items-center justify-between" data-id="element-240">
                            <div className="text-white/40 text-xs font-['Montserrat']" data-id="element-241">
                              {lastUpdatedBy && <>
                                  Last updated by {lastUpdatedBy}
                                  {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                                </>}
                            </div>
                            <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-242">
                              {isSavingNotes ? 'Saving...' : 'Save Notes'}
                            </button>
                          </div>
                        </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-243">
                          <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-244">
                            {meetingNotesText || <span className="italic text-white/40" data-id="element-245">
                                No meeting notes available.
                              </span>}
                          </p>
                          {lastUpdatedBy && <div className="mt-3 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-246">
                              Last updated by {lastUpdatedBy}
                              {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                            </div>}
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Feed */}
            <div className="space-y-6" data-id="element-247">
              {posts.length === 0 ? <div className="text-center py-10 text-white/40 font-['Montserrat']" data-id="element-248">
                  No posts yet.
                </div> : posts.map((post, idx) => <motion.div key={post.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + idx * 0.1
            }} className={`backdrop-blur-md rounded-2xl p-5 md:p-6 ${post.pinned ? 'bg-black/40 border border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.2)]' : 'bg-black/40 border border-white/5'}`} data-id="element-249">
                    {post.pinned && <div className="flex items-center gap-2 text-purple-400 text-xs font-bold font-['Montserrat'] mb-3 uppercase tracking-wider" data-id="element-250">
                        📌 Pinned Post
                      </div>}
                    <div className="flex items-center gap-3 mb-4" data-id="element-251">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm" data-id="element-252">
                        {post.authorInitials}
                      </div>
                      <div className="flex-1" data-id="element-253">
                        <h4 className="text-white font-semibold font-['Montserrat']" data-id="element-254">
                          {post.authorName}
                        </h4>
                        <p className="text-white/40 text-xs" data-id="element-255">
                          {post.createdAt ? safeToDate(post.createdAt)?.toLocaleString() ?? 'Just now' : 'Just now'}
                        </p>
                      </div>
                    </div>

                    {post.content && <p className={`leading-relaxed mb-4 whitespace-pre-wrap ${post.fontSize === 'sm' ? 'text-sm' : post.fontSize === 'lg' ? 'text-lg' : post.fontSize === 'xl' ? 'text-xl' : post.fontSize === '2xl' ? 'text-2xl' : 'text-sm md:text-base'}`} style={{
                color: post.fontColor || 'rgba(255,255,255,0.8)'
              }} data-id="element-256">
                        {post.content}
                      </p>}

                    <div className="mb-4 flex items-center gap-3" data-id="element-257">
                      <button onClick={() => user && handleLike(post)} className={`flex items-center gap-1.5 text-sm font-['Montserrat'] transition-all ${!user ? 'text-white/30 cursor-default' : post.likes?.some(l => l.email === user.email) ? 'text-pink-400' : 'text-white/40 hover:text-pink-400'}`} data-id="element-258">
                        <span className="text-lg" data-id="element-259">
                          {user && post.likes?.some(l => l.email === user.email) ? '❤️' : '🤍'}
                        </span>
                        <span data-id="element-260">{post.likes?.length || 0}</span>
                      </button>
                      {(post.likes?.length || 0) > 0 && <p className="text-white/30 text-xs font-['Montserrat']" data-id="element-261">
                          Liked by {post.likes?.map(l => l.name).join(', ')}
                        </p>}
                    </div>

                    <div className="border-t border-white/10 pt-4 space-y-4" data-id="element-262">
                      {post.comments?.map((comment, cIdx) => <div key={cIdx} className="flex gap-3" data-id="element-263">
                          <div className="w-8 h-8 rounded-full bg-pink-500/50 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" data-id="element-264">
                            {comment.authorInitials}
                          </div>
                          <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 flex-1" data-id="element-265">
                            <div className="flex justify-between items-baseline mb-1" data-id="element-266">
                              <h5 className="text-white/90 text-sm font-semibold" data-id="element-267">
                                {comment.authorName}
                              </h5>
                              <div className="flex items-center gap-2" data-id="element-268">
                                <span className="text-white/30 text-[10px]" data-id="element-269">
                                  {comment.createdAt ? safeToDate(comment.createdAt)?.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) ?? '' : ''}
                                </span>
                                {user && comment.authorName === (displayName || user.email?.split('@')[0] || '') && <button onClick={() => handleDeleteComment(post.id, comment)} className="text-white/20 hover:text-red-400 transition-colors text-xs" title="Delete comment" data-id="element-270">
                                      ✕
                                    </button>}
                              </div>
                            </div>
                            <p className="text-white/70 text-sm whitespace-pre-wrap" data-id="element-271">
                              {comment.content}
                            </p>
                          </div>
                        </div>)}

                      {user && <div className="flex gap-3 items-center mt-4" data-id="element-272">
                          <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-white/50 text-xs font-bold" data-id="element-273">
                            {displayName ? displayName.substring(0, 2).toUpperCase() : user.email?.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 flex gap-2" data-id="element-274">
                            <input type="text" value={commentInputs[post.id] || ''} onChange={e => setCommentInputs(prev => ({
                      ...prev,
                      [post.id]: e.target.value
                    }))} onKeyDown={e => e.key === 'Enter' && handleComment(post.id)} placeholder="Add a comment..." className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none placeholder-white/30" data-id="element-275" />
                            <button onClick={() => handleComment(post.id)} disabled={!commentInputs[post.id]?.trim()} className="text-purple-400 hover:text-purple-300" data-id="element-276">
                              Post
                            </button>
                          </div>
                        </div>}
                    </div>
                  </motion.div>)}
            </div>
          </div>

          {/* Right Column: Meeting Notes Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0" data-id="element-277">
            <div className="sticky top-28 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-278">
              <div className="p-4 bg-white/5 border-b border-white/10" data-id="element-279">
                <h3 className="font-['Montserrat'] font-bold text-lg text-white flex items-center gap-2" data-id="element-280">
                  📝 Weekly Meeting Notes
                </h3>
              </div>
              <div className="p-4 space-y-4" data-id="element-281">
                {user && canPost ? <div className="flex flex-col gap-3" data-id="element-282">
                    <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[200px] font-['Inter']" data-id="element-283" />
                    <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="w-full px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-284">
                      {isSavingNotes ? 'Saving...' : 'Save Notes'}
                    </button>
                    {lastUpdatedBy && <div className="text-white/40 text-xs font-['Montserrat'] text-center mt-2" data-id="element-285">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-286" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-287">
                    <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-288">
                      {meetingNotesText || <span className="italic text-white/40" data-id="element-289">
                          No meeting notes available.
                        </span>}
                    </p>
                    {lastUpdatedBy && <div className="mt-4 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-290">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-291" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ── Public Pages ────────────────────────────────────────────────
function HomePage({
  onNavigate
}: {
  onNavigate: (page: PageId) => void;
}) {
  return <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 pt-20" data-id="element-292">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1
    }} className="text-center max-w-4xl mx-auto" data-id="element-293">
        <h1 className="font-['Montserrat'] font-black text-7xl md:text-9xl tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-purple-200 to-purple-400 drop-shadow-[0_0_40px_rgba(168,85,247,0.4)]" data-id="element-294">
          GSF
        </h1>
        <p className="font-['Montserrat'] font-bold text-2xl md:text-4xl tracking-[0.35em] text-white/70 mt-2 mb-10" data-id="element-295">
          A G E N C Y
        </p>

        {/* Mission Statement Glass Box */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-8 md:p-10 max-w-2xl mx-auto mb-8" data-id="element-296">
          <p className="font-['Inter'] text-base md:text-lg text-white/80 leading-relaxed italic" data-id="element-297">
            "Our mission is to build a team where every voice matters, every
            dream is supported, and every person is given the chance to shine.
            Through belief, encouragement, and unity, we lift each other
            higher—reaching not just for success, but for the stars."
          </p>
        </motion.div>

        <ActionButtons currentPage="home" onNavigate={onNavigate} data-id="element-298" />
      </motion.div>
    </div>;
}
function AboutPage() {
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-299">
      <div className="max-w-4xl mx-auto" data-id="element-300">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-center text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-8" data-id="element-301">
          About GSF Agency
        </motion.h2>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-8 md:p-12 text-left space-y-6" data-id="element-302">
          <p className="text-white/90 font-['Inter'] text-xl font-semibold leading-relaxed" data-id="element-303">
            At GSF Agency, we're more than just a team — we're a community.
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-304">
            We bring together creators from all walks of life — from live
            streamers and entertainers to life coaches, artists, and
            entrepreneurs — all working toward one goal: growth.
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-305">
            Our mission is simple: To build a space where every voice matters,
            every dream is supported, and every person has the opportunity to
            shine.
          </p>
          <div data-id="element-306">
            <p className="text-white/90 font-['Inter'] text-lg font-semibold mb-3" data-id="element-307">
              We believe in:
            </p>
            <ul className="space-y-2 text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-308">
              <li className="flex items-start gap-2" data-id="element-309">
                <span className="text-purple-400 mt-1" data-id="element-310">💜</span>
                <span data-id="element-311">Real support (not fake hype)</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-312">
                <span className="text-purple-400 mt-1" data-id="element-313">💜</span>
                <span data-id="element-314">Learning from each other</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-315">
                <span className="text-purple-400 mt-1" data-id="element-316">💜</span>
                <span data-id="element-317">Growing together, not competing against each other</span>
              </li>
            </ul>
          </div>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-318">
            Whether you're just starting out or already building momentum, GSF
            is a place where you can level up with people who actually want to
            see you win.
          </p>
        </motion.div>
      </div>
    </div>;
}
function WhyGSFPage() {
  const reasons = [{
    title: '✨ We actually help you grow',
    desc: 'We guide you through content, live strategy, and finding your niche so you can build something sustainable.'
  }, {
    title: "✨ You don't have to figure it out alone",
    desc: "We've got experienced creators who've been doing this for years, and they're here to help you."
  }, {
    title: '✨ Community over competition',
    desc: "We don't gatekeep. We share knowledge, support each other, and celebrate wins together."
  }, {
    title: '✨ Multiple niches, one team',
    desc: 'Music artists, streamers, life coaches, DJs, travelers — we bring different worlds together.'
  }, {
    title: '✨ No cost to join',
    desc: 'You get access to knowledge, support, and opportunity without paying to be here.'
  }, {
    title: '✨ Real people. Real growth.',
    desc: 'No fake promises. Just consistency, support, and results over time.'
  }];
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-319">
      <div className="max-w-5xl mx-auto" data-id="element-320">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-center text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-321">
          💜 Why Choose GSF?
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="text-center text-white/80 font-['Inter'] text-xl mb-4" data-id="element-322">
          Because this isn't just another agency.
        </motion.p>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="text-center text-white/70 font-['Inter'] text-lg mb-12" data-id="element-323">
          At GSF, you're not just a number — you're part of something real.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-324">
          {reasons.map((r, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3 + i * 0.1
        }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-8 hover:border-purple-500/30 transition-all" data-id="element-325">
              <h3 className="font-['Montserrat'] font-bold text-2xl text-white mb-4" data-id="element-326">
                {r.title}
              </h3>
              <p className="text-white/70 font-['Inter'] leading-relaxed" data-id="element-327">
                {r.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>;
}
function ServicesPage() {
  const services = [{
    emoji: '🎥',
    title: 'Live Streaming Support',
    bullets: ['Learn how to run engaging lives', 'Understand gifting, engagement, and retention', 'Build real connections with your audience']
  }, {
    emoji: '📈',
    title: 'Growth Strategy',
    bullets: ['Help finding your niche', 'Content direction based on YOUR strengths', 'Personalized guidance (not cookie-cutter advice)']
  }, {
    emoji: '🤝',
    title: 'Community & Networking',
    bullets: ['Connect with other creators', 'Collaborate and cross-grow', 'Be part of a supportive environment']
  }, {
    emoji: '🎯',
    title: 'Creator Development',
    bullets: ['Confidence building on camera', 'Branding and identity help', 'Leveling up your presence']
  }, {
    emoji: '💡',
    title: 'Monetization Guidance',
    bullets: ['Understanding how to earn through lives', 'Building consistency for income growth', 'Learning what actually converts']
  }, {
    emoji: '🌍',
    title: 'Team Opportunities',
    bullets: ['Grow into leadership roles', 'Build your own team under GSF', 'Expand beyond just being a creator']
  }];
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-328">
      <div className="max-w-5xl mx-auto" data-id="element-329">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-center text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-330">
          🚀 What We Offer
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="text-center text-white/80 font-['Inter'] text-xl mb-12" data-id="element-331">
          We help creators grow, connect, and turn their content into something
          sustainable.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-332">
          {services.map((s, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2 + i * 0.1
        }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-8 hover:border-purple-500/30 transition-all" data-id="element-333">
              <div className="text-4xl mb-4" data-id="element-334">{s.emoji}</div>
              <h3 className="font-['Montserrat'] font-bold text-xl text-white mb-4" data-id="element-335">
                {s.title}
              </h3>
              <ul className="space-y-2" data-id="element-336">
                {s.bullets.map((b, idx) => <li key={idx} className="text-white/70 font-['Inter'] leading-relaxed flex items-start gap-2" data-id="element-337">
                    <span className="text-purple-400 mt-0.5" data-id="element-338">•</span>
                    <span data-id="element-339">{b}</span>
                  </li>)}
              </ul>
            </motion.div>)}
        </div>

        {/* Tagline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="text-center mt-16" data-id="element-340">
          <p className="font-['Montserrat'] font-bold text-xl md:text-2xl text-white/60 italic leading-relaxed" data-id="element-341">
            "Not just content. Not just lives.
            <br data-id="element-342" />A community built to help you win."
          </p>
        </motion.div>
      </div>
    </div>;
}
function ContactPage({
  onNavigate
}: {
  onNavigate: (page: PageId) => void;
}) {
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-343">
      <div className="max-w-2xl mx-auto text-center" data-id="element-344">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-6" data-id="element-345">
          Join GSF Agency
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="text-white/70 font-['Inter'] text-lg mb-8" data-id="element-346">
          Ready to grow with a community that actually supports you? Tap below
          to join.
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-10" data-id="element-347">
          <a href="https://www.tiktok.com/t/ZP9NNQHXN2VbK-kwV38/" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-lg font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] transition-all hover:scale-105" data-id="element-348">
            Join GSF Agency
          </a>
        </motion.div>

        {/* Agency Transfer Warning */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-8 md:p-10 text-left" data-id="element-349">
          <p className="text-white/90 font-['Inter'] text-base leading-relaxed mb-4" data-id="element-350">
            <span className="text-yellow-400 font-bold" data-id="element-351">
              ⚠️ Already in another agency?
            </span>{' '}
            You'll need to request to leave your current agency first. After
            leaving, there's a{' '}
            <span className="font-bold" data-id="element-352">60-day waiting period</span> before you
            can apply to a new one.
          </p>
          <p className="text-white/90 font-['Inter'] text-base leading-relaxed" data-id="element-353">
            However, if you're already in the process of leaving and want to
            come over to GSF —{' '}
            <span className="text-white/90 underline" data-id="element-354">
              reach out to any of our agency agents
            </span>{' '}
            listed on the{' '}
            <button onClick={() => onNavigate('team')} className="text-purple-400 hover:text-purple-300 font-semibold underline transition-colors" data-id="element-355">
              Meet the Team
            </button>{' '}
            page. We can start taking you under our wing immediately. We don't
            believe in turning our backs on people.
          </p>
        </motion.div>

        {/* Meet the Owner & Agents */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="mt-16" data-id="element-356">
          <h3 className="font-['Montserrat'] font-black text-2xl md:text-3xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-8" data-id="element-357">
            Meet the Owner & Coaches
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-358">
            {[{
            name: 'STARPLAYER757',
            title: 'Owner / Founder',
            photo: 'https://cdn.magicpatterns.com/uploads/nJv9m1EvDb3Ror2uTufs4S/file_00000000d63c71f78003d43c49e6b2d1.png',
            tiktok: 'https://www.tiktok.com/@zoeaking?_r=1&_t=ZP-95d2CTbttLr'
          }, {
            name: 'ZettaMINDset',
            title: 'Director of GSF / QVC',
            photo: 'https://cdn.magicpatterns.com/uploads/mqTALxa5F64M2yQL8KH6YU/file_0000000036f871fdb97ff10845e446c2.png',
            tiktok: 'https://www.tiktok.com/@mszetta?_r=1&_t=ZP-95d2AQ9iXwU'
          }, {
            name: 'BaddieDaBoss',
            title: 'Director of Coordination',
            photo: 'https://cdn.magicpatterns.com/uploads/d1X4vJ4wsQY57gLp4vuEUt/file_00000000fdd871f7917e9b43635bb53e.png',
            tiktok: 'https://www.tiktok.com/@nfskmx26?_r=1&_t=ZP-95d239RoLCZ'
          }, {
            name: 'Trippin.with.mandy',
            title: 'GSF Agent / Coach',
            photo: 'https://cdn.magicpatterns.com/uploads/w6gKF6k398cAmKSJxkiTZK/file_00000000464871f78b5d9742c72cca65.png',
            tiktok: 'https://www.tiktok.com/@trippin.with.mandy'
          }, {
            name: 'J3',
            title: 'GSF Agent',
            photo: 'https://cdn.magicpatterns.com/uploads/kHPVAQbDpnt8Zdza5GNRAF/file_00000000dfc071fdaf303e4b92a454de.png',
            tiktok: 'https://www.tiktok.com/@chyyy..0110?_r=1&_t=ZP-95d27IRtmj1'
          }].map((person, i) => <motion.a key={person.name} href={person.tiktok} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8 + i * 0.1
          }} whileHover={{
            scale: 1.05,
            y: -5
          }} className="flex flex-col items-center group" data-id="element-359">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-2 border-purple-500/40 group-hover:border-purple-400 group-hover:shadow-[0_0_20px_rgba(168,85,247,0.4)] transition-all mb-3" data-id="element-360">
                  <img src={person.photo} alt={person.name} className="w-full h-full object-cover" data-id="element-361" />
                </div>
                <h4 className="font-['Montserrat'] font-bold text-white text-sm md:text-base text-center group-hover:text-purple-300 transition-colors" data-id="element-362">
                  {person.name}
                </h4>
                <p className="text-white/40 text-xs md:text-sm font-['Inter'] text-center" data-id="element-363">
                  {person.title}
                </p>
                <span className="text-purple-400/60 text-xs mt-1 group-hover:text-purple-300 transition-colors flex items-center gap-1" data-id="element-364">
                  <ExternalLinkIcon size={10} data-id="element-365" /> TikTok
                </span>
              </motion.a>)}
          </div>
        </motion.div>
      </div>
    </div>;
}
// ── Main App ────────────────────────────────────────────────────
export function App() {
  const [currentPage, setCurrentPage] = useState<PageId>('home');
  const [agentUser, setAgentUser] = useState<User | null>(null);
  const [agentDisplayName, setAgentDisplayName] = useState('');
  const [agentRole, setAgentRole] = useState('');
  const [creatorUser, setCreatorUser] = useState<User | null>(null);
  const [creatorDisplayName, setCreatorDisplayName] = useState('');
  const [creatorRole, setCreatorRole] = useState('');
  useEffect(() => {
    const unsubscribeAgent = onAuthStateChanged(auth, async user => {
      setAgentUser(user);
      if (user) {
        try {
          const docSnap = await getDoc(doc(db, 'users', user.uid));
          if (docSnap.exists()) {
            const data = docSnap.data();
            setAgentDisplayName(data?.Name || data?.name || user.email?.split('@')[0] || '');
            setAgentRole(data?.role || 'creator');
          } else {
            setAgentDisplayName(user.email?.split('@')[0] || '');
          }
        } catch (e) {
          setAgentDisplayName(user.email?.split('@')[0] || '');
        }
      }
    });
    const unsubscribeCreator = onAuthStateChanged(creatorAuth, async user => {
      setCreatorUser(user);
      if (user) {
        try {
          const docSnap = await getDoc(doc(db, 'users', user.uid));
          if (docSnap.exists()) {
            const data = docSnap.data();
            setCreatorDisplayName(data?.Name || data?.name || user.email?.split('@')[0] || '');
            setCreatorRole(data?.role || 'creator');
          } else {
            setCreatorDisplayName(user.email?.split('@')[0] || '');
          }
        } catch (e) {
          setCreatorDisplayName(user.email?.split('@')[0] || '');
        }
      }
    });
    return () => {
      unsubscribeAgent();
      unsubscribeCreator();
    };
  }, []);
  const handleAgentLogin = (displayName: string, role: string) => {
    setAgentDisplayName(displayName);
    setAgentRole(role);
    setCurrentPage('portal-hub');
  };
  const handleCreatorLogin = (displayName: string, role: string) => {
    setCreatorDisplayName(displayName);
    setCreatorRole(role);
    setCurrentPage('creator-hub');
  };
  const handleAgentSignOut = async () => {
    await signOut(auth);
    setAgentUser(null);
    setAgentDisplayName('');
    setAgentRole('');
    setCurrentPage('home');
  };
  const handleCreatorSignOut = async () => {
    await signOut(creatorAuth);
    setCreatorUser(null);
    setCreatorDisplayName('');
    setCreatorRole('');
    setCurrentPage('home');
  };
  return <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden font-['Inter']" data-id="element-366">
      {/* Galaxy Background */}
      <div className="fixed inset-0 z-0 overflow-hidden" data-id="element-367">
        {/* Nebula base */}
        <div className="absolute inset-0 bg-nebula animate-nebula" data-id="element-368" />
        {/* Stardust texture */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20" data-id="element-369" />
        {/* Galaxy spiral arms */}
        <div className="absolute w-[200%] h-[200%] top-1/2 left-1/2 galaxy-swirl-1" data-id="element-370">
          <div className="absolute inset-0 spiral-arm-layer opacity-40" data-id="element-371" />
        </div>
        <div className="absolute w-[180%] h-[180%] top-1/2 left-1/2 galaxy-swirl-2" data-id="element-372">
          <div className="absolute inset-0 spiral-arm-layer-2 opacity-30" data-id="element-373" />
        </div>
        <div className="absolute w-[160%] h-[160%] top-1/2 left-1/2 galaxy-swirl-3" data-id="element-374">
          <div className="absolute inset-0 spiral-arm-layer-3 opacity-25" data-id="element-375" />
        </div>
        {/* Galaxy core glow */}
        <div className="absolute top-1/2 left-1/2 w-[40vmax] h-[40vmax] rounded-full galaxy-core" style={{
        background: 'radial-gradient(circle, rgba(168,85,247,0.4) 0%, rgba(138,43,226,0.2) 30%, transparent 70%)',
        transform: 'translate(-50%, -50%)'
      }} data-id="element-376" />
      </div>

      <SparkleOverlay data-id="element-377" />

      {/* Only show navbar on public pages */}
      {!['agent-login', 'creator-login', 'portal-hub', 'creator-hub', 'portal', 'portal-qvc', 'creator-portal', 'creator-portal-qvc'].includes(currentPage) && <Navbar currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-378" />}

      <AnimatePresence mode="wait" data-id="element-379">
        {currentPage === 'home' && <motion.div key="home" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-380">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-381" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-382" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-383" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-384">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-385" />
            </div>
            <HomePage onNavigate={setCurrentPage} data-id="element-386" />
          </motion.div>}
        {currentPage === 'about' && <motion.div key="about" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-387">
            <img src="https://cdn.magicpatterns.com/uploads/kWDpTLGK7NY5fv9v1Z19LW/file_00000000303c71f59049384ce02a45cd.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-388" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-389" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-390" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-391">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-392" />
            </div>
            <AboutPage data-id="element-393" />
          </motion.div>}
        {currentPage === 'team' && <motion.div key="team" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-394">
            <img src="https://cdn.magicpatterns.com/uploads/kWDpTLGK7NY5fv9v1Z19LW/file_00000000303c71f59049384ce02a45cd.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-395" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-396" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-397" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-398">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-399" />
            </div>
            <MeetTheTeam data-id="element-400" />
          </motion.div>}
        {currentPage === 'why' && <motion.div key="why" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-401">
            <img src="https://cdn.magicpatterns.com/uploads/foWSAu7Y3A45z451jtnA5U/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-402" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-403" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-404" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-405">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-406" />
            </div>
            <WhyGSFPage data-id="element-407" />
          </motion.div>}
        {currentPage === 'services' && <motion.div key="services" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-408">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-409" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-410" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-411" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-412">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-413" />
            </div>
            <ServicesPage data-id="element-414" />
          </motion.div>}
        {currentPage === 'weekly-schedules' && <motion.div key="weekly-schedules" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-415">
            <img src="https://cdn.magicpatterns.com/uploads/foWSAu7Y3A45z451jtnA5U/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-416" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-417" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-418" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-419">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-420" />
            </div>
            <LiveSchedules onNavigate={setCurrentPage} data-id="element-421" />
          </motion.div>}
        {currentPage === 'contact' && <motion.div key="contact" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-422">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-423" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-424" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-425" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-426">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-427" />
            </div>
            <ContactPage onNavigate={setCurrentPage} data-id="element-428" />
          </motion.div>}

        {/* Schedule Pages */}
        {currentPage === 'schedule-prince-justice' && <motion.div key="schedule-prince-justice" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-429">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-430" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-431" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-432" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-433">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-434" />
            </div>
            <AgentSchedulePage agentName="Prince Justice" category="Engagement Lives" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'Mon–Sat',
          time: 'Pop-Up Lives',
          type: '🔔 Look out for pop lives!',
          notes: "This streamer often does pop-ups — keep notifications on so you don't miss out!"
        }, {
          day: 'Sunday',
          time: '3:00 PM EST',
          type: 'Engage & Vibe',
          notes: 'Come and hang while we engage with other quality creators. Consider this a meet & greet while you sit back and vibe.'
        }]} data-id="element-435" />
          </motion.div>}

        {currentPage === 'schedule-baddie' && <motion.div key="schedule-baddie" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-436">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-437" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-438" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-439" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-440">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-441" />
            </div>
            <AgentSchedulePage agentName="Baddie" category="Engagement & After Party Vibes" onBack={() => setCurrentPage('weekly-schedules')} bannerImage="https://cdn.magicpatterns.com/uploads/obbQo4ZzunT2uKQdUHkcqi/file_0000000077dc71fd80d307ca3051e46f.png" tiktokUrl="https://www.tiktok.com/@nfskmx26" scheduleData={[{
          day: 'Monday',
          time: 'Coming Soon',
          type: '🎯 Engagement Live',
          notes: 'Engagement lives coming soon!'
        }, {
          day: 'Tuesday',
          time: 'Nightly',
          type: '🌙 Star Lounge After Party',
          notes: 'Every night after the club we go there to hang, talk, and sometimes listen to music.'
        }, {
          day: 'Wednesday',
          time: 'Coming Soon',
          type: '🎯 Engagement Live',
          notes: 'Engagement lives coming soon!'
        }, {
          day: 'Thursday',
          time: 'Nightly',
          type: '🌙 Star Lounge After Party',
          notes: 'Every night after the club we go there to hang, talk, and sometimes listen to music.'
        }, {
          day: 'Friday',
          time: 'Coming Soon',
          type: '🎯 Engagement Live',
          notes: 'Engagement lives coming soon!'
        }, {
          day: 'Saturday',
          time: 'Nightly',
          type: '🌙 Star Lounge After Party',
          notes: 'Every night after the club we go there to hang, talk, and sometimes listen to music.'
        }, {
          day: 'Sunday',
          time: 'Nightly',
          type: '🌙 Star Lounge After Party',
          notes: 'Every night after the club we go there to hang, talk, and sometimes listen to music.'
        }]} data-id="element-442" />
          </motion.div>}

        {currentPage === 'schedule-star-dj' && <motion.div key="schedule-star-dj" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-443">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-444" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-445" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-446" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-447">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-448" />
            </div>
            <AgentSchedulePage agentName="Star's Pad Lounge" category="DJ music jokes games and fun" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'Monday',
          time: '8:00 PM EST',
          type: 'Mind Body & Souls Night',
          notes: 'Get there early to grab a spot on the panel — they fill up fast! When your turn is up, let Star know what feeds your soul, then request a song. Lots of fun!'
        }, {
          day: 'Tuesday',
          time: '8:00 PM EST',
          type: 'Trivia Q&A',
          notes: "Get there early to grab a spot on the panel — they fill up fast! When your turn is up, answer trivia questions then request a song. It's a blast!"
        }, {
          day: 'Wednesday',
          time: '8:00 PM EST',
          type: 'Chill & Vibe Day',
          notes: 'Low music while we sit back and chat — relaxed vibes all night'
        }, {
          day: 'Thursday',
          time: '8:00 PM EST',
          type: 'Caribbean Night',
          notes: 'Come on over while we play Caribbean music with DJ FiveSix Player'
        }, {
          day: 'Friday',
          time: '8:00 PM EST',
          type: 'Chill & Vibe Day',
          notes: 'Low music while we sit back and chat — relaxed vibes all night'
        }, {
          day: 'Saturday',
          time: '10:00 PM EST',
          type: 'Club Night',
          notes: "Star's Pad Lounge music & often requests — it's a whole vibe! Get there early to snag a chance to be on the panel!"
        }, {
          day: 'Sunday',
          time: '10:00 PM EST',
          type: 'Club Night',
          notes: "Star's Pad Lounge music & often requests — it's a whole vibe! Get there early to snag a chance to be on the panel!"
        }]} data-id="element-449" />
          </motion.div>}

        {currentPage === 'schedule-nessy' && <motion.div key="schedule-nessy" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-450">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-451" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-452" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-453" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-454">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-455" />
            </div>
            <AgentSchedulePage agentName="Nessy" category="Music Related Lives" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'Monday',
          time: '6:00 PM EST',
          type: '🔔 Pop-Up Live',
          notes: 'Keep notifications on — Nessy may pop up!'
        }, {
          day: 'Wednesday',
          time: '10:00 PM EST',
          type: 'Music Live',
          notes: 'Mid-week music session'
        }, {
          day: 'Thursday',
          time: '10:00 PM EST',
          type: 'Music Live',
          notes: 'Late night music vibes'
        }]} data-id="element-456" />
          </motion.div>}

        {currentPage === 'schedule-drika' && <motion.div key="schedule-drika" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-457">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-458" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-459" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-460" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-461">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-462" />
            </div>
            <AgentSchedulePage agentName="Drika" category="Music Related Lives" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'Monday',
          time: '10:00 PM - 12:00 AM EST',
          type: 'Music Live',
          notes: 'Daily music & vibe stream'
        }, {
          day: 'Tuesday',
          time: '10:00 PM - 12:00 AM EST',
          type: 'Music Live',
          notes: 'Daily music & vibe stream'
        }, {
          day: 'Wednesday',
          time: '10:00 PM - 12:00 AM EST',
          type: 'Music Live',
          notes: 'Daily music & vibe stream'
        }, {
          day: 'Thursday',
          time: '10:00 PM - 12:00 AM EST',
          type: 'Music Live',
          notes: 'Daily music & vibe stream'
        }, {
          day: 'Friday',
          time: '10:00 PM - 12:00 AM EST',
          type: 'Music Live',
          notes: 'Daily music & vibe stream'
        }, {
          day: 'Saturday',
          time: '10:00 PM - 12:00 AM EST',
          type: 'Music Live',
          notes: 'Daily music & vibe stream'
        }, {
          day: 'Sunday',
          time: '10:00 PM - 12:00 AM EST',
          type: 'Music Live',
          notes: 'Daily music & vibe stream'
        }]} data-id="element-463" />
          </motion.div>}

        {currentPage === 'schedule-mandy' && <motion.div key="schedule-mandy" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-464">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-465" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-466" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-467" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-468">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-469" />
            </div>
            <AgentSchedulePage agentName="Trippin with Mandy" category="Coaching Lives" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'All Week',
          time: 'Pop-Up Lives',
          type: '🔔 Look out for pop-up lives!',
          notes: "Mandy often does pop-up lives — keep notifications on so you don't miss out!"
        }, {
          day: 'Monday',
          time: '10:00 AM EST',
          type: 'Mindset Monday',
          notes: 'Weekly motivation & goal setting'
        }, {
          day: 'Tuesday',
          time: '10:00 AM EST',
          type: 'Travel Tuesday',
          notes: 'Explore the world, share travel stories & discover new destinations together'
        }, {
          day: 'Wednesday',
          time: '10:00 AM EST',
          type: 'Wellness Wednesday',
          notes: 'Mental health & self-care'
        }, {
          day: 'Thursday',
          time: '10:00 AM EST',
          type: 'Thriving Thursday',
          notes: 'We talk about growth, ways to make money, and side hustles'
        }, {
          day: 'Friday',
          time: '10:00 AM EST',
          type: 'Free Friday',
          notes: 'A wildcard day — explore the freedom of travel, finding inner peace, or breaking free from what holds you back'
        }, {
          day: 'Saturday',
          time: 'OFF',
          type: 'Day Off',
          notes: 'Weekend rest'
        }, {
          day: 'Sunday',
          time: 'OFF',
          type: 'Rest Day',
          notes: 'Recharge & prep for next week'
        }]} data-id="element-470" />
          </motion.div>}

        {currentPage === 'schedule-zetta' && <motion.div key="schedule-zetta" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-471">
            <img src="https://cdn.magicpatterns.com/uploads/foWSAu7Y3A45z451jtnA5U/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-472" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-473" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-474" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-475">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-476" />
            </div>
            <AgentSchedulePage agentName="ZettaMINDset" category="Engagement & Chill Vibes" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'Monday',
          time: '7:00 PM - 9:00 PM EST',
          type: 'Mindset Live Stream',
          notes: 'Mindset live stream'
        }, {
          day: 'Tuesday',
          time: '7:00 PM - 9:00 PM EST',
          type: 'Mindset Live Stream',
          notes: 'Mindset live stream'
        }, {
          day: 'Wednesday',
          time: '7:00 PM - 9:00 PM EST',
          type: 'Mindset Live Stream',
          notes: 'Mindset live stream'
        }, {
          day: 'Thursday',
          time: '6:00 PM - 7:00 PM EST',
          type: 'Battling',
          notes: 'Battle live'
        }, {
          day: 'Thursday',
          time: '7:00 PM - 8:00 PM EST',
          type: 'Mindset Live Stream',
          notes: 'Mindset live stream'
        }, {
          day: 'Sunday',
          time: '4:00 PM - 6:00 PM EST',
          type: 'Mindset Live Stream',
          notes: 'Mindset live stream'
        }]} data-id="element-477" />
          </motion.div>}

        {currentPage === 'schedule-jdolla' && <motion.div key="schedule-jdolla" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-478">
            <img src="https://cdn.magicpatterns.com/uploads/foWSAu7Y3A45z451jtnA5U/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-479" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-480" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-481" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-482">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-483" />
            </div>
            <AgentSchedulePage agentName="IAM_JDOLLA" category="Chill & Vibe Lives" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'All Week',
          time: 'Pop-Up Lives',
          type: '🔔 Look out for pop-up lives!',
          notes: "J_Dolla often does pop-up lives — keep notifications on so you don't miss out!"
        }]} data-id="element-484" />
          </motion.div>}

        {currentPage === 'schedule-j3' && <motion.div key="schedule-j3" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-485">
            <img src="https://cdn.magicpatterns.com/uploads/foWSAu7Y3A45z451jtnA5U/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-486" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-487" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-488" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-489">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-490" />
            </div>
            <AgentSchedulePage agentName="J3" category="Chill & Vibe Lives" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'All Week',
          time: 'Pop-Up Lives',
          type: '🔔 Look out for pop-up lives!',
          notes: "J3 often does pop-up lives — keep notifications on so you don't miss out!"
        }]} data-id="element-491" />
          </motion.div>}

        {currentPage === 'schedule-shorty' && <motion.div key="schedule-shorty" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-492">
            <img src="https://cdn.magicpatterns.com/uploads/foWSAu7Y3A45z451jtnA5U/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-493" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-494" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-495" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-496">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-497" />
            </div>
            <AgentSchedulePage agentName="Shorty713" category="Chill & Vibe Lives" onBack={() => setCurrentPage('weekly-schedules')} scheduleData={[{
          day: 'All Week',
          time: 'Pop-Up Lives',
          type: '🔔 Look out for pop-up lives!',
          notes: "Shorty713 often does pop-up lives — keep notifications on so you don't miss out!"
        }]} data-id="element-498" />
          </motion.div>}

        {/* Login Pages */}
        {currentPage === 'agent-login' && <motion.div key="agent-login" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-499">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-500" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-501" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-502" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-503">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-504" />
            </div>
            <div className="absolute top-6 left-6 z-50" data-id="element-505">
              <button onClick={() => setCurrentPage('home')} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-506">
                ← Back to Home
              </button>
            </div>
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-507">
              <div className="max-w-md w-full" data-id="element-508">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-509">
                  Agent Portal Login
                </h2>
                <LoginPage title="Agent Portal Login" type="agent" onLogin={handleAgentLogin} data-id="element-510" />
              </div>
            </div>
          </motion.div>}
        {currentPage === 'creator-login' && <motion.div key="creator-login" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-511">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-512" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-513" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-514" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-515">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-516" />
            </div>
            <div className="absolute top-6 left-6 z-50" data-id="element-517">
              <button onClick={() => setCurrentPage('home')} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-518">
                ← Back to Home
              </button>
            </div>
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-519">
              <div className="max-w-md w-full" data-id="element-520">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-521">
                  Creator Portal Login
                </h2>
                <LoginPage title="Creator Portal Login" type="creator" onLogin={handleCreatorLogin} data-id="element-522" />
              </div>
            </div>
          </motion.div>}

        {/* Portal Hubs */}
        {currentPage === 'portal-hub' && <motion.div key="portal-hub" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-523">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-524" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-525" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-526" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-527">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-528" />
            </div>
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-529">
              <div className="absolute top-6 left-6 z-50" data-id="element-530">
                <button onClick={handleAgentSignOut} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-531">
                  ← Sign Out
                </button>
              </div>
              <div className="max-w-4xl w-full" data-id="element-532">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-533">
                  Select Portal
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-534">
                  <button onClick={() => setCurrentPage('portal')} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-10 hover:border-purple-500/50 hover:bg-white/5 transition-all group text-left" data-id="element-535">
                    <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 group-hover:scale-105 transition-transform origin-left" data-id="element-536">
                      GSF Live
                    </h3>
                    <p className="text-white/50 font-['Inter']" data-id="element-537">
                      Access the main GSF agency portal and communications.
                    </p>
                  </button>
                  <button onClick={() => setCurrentPage('portal-qvc')} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-10 hover:border-cyan-500/50 hover:bg-white/5 transition-all group text-left" data-id="element-538">
                    <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-4 group-hover:scale-105 transition-transform origin-left" data-id="element-539">
                      GSF / QVC Live
                    </h3>
                    <p className="text-white/50 font-['Inter']" data-id="element-540">
                      Access the QVC division portal and communications.
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}

        {currentPage === 'creator-hub' && <motion.div key="creator-hub" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-541">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-542" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-543" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-544" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-545">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-546" />
            </div>
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-547">
              <div className="absolute top-6 left-6 z-50" data-id="element-548">
                <button onClick={handleCreatorSignOut} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-549">
                  ← Sign Out
                </button>
              </div>
              <div className="max-w-4xl w-full" data-id="element-550">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-551">
                  Select Portal
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-552">
                  <button onClick={() => setCurrentPage('creator-portal')} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-10 hover:border-purple-500/50 hover:bg-white/5 transition-all group text-left" data-id="element-553">
                    <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 group-hover:scale-105 transition-transform origin-left" data-id="element-554">
                      GSF Creator Portal
                    </h3>
                    <p className="text-white/50 font-['Inter']" data-id="element-555">
                      Access the main GSF creator updates and resources.
                    </p>
                  </button>
                  <button onClick={() => setCurrentPage('creator-portal-qvc')} className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-10 hover:border-cyan-500/50 hover:bg-white/5 transition-all group text-left" data-id="element-556">
                    <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-4 group-hover:scale-105 transition-transform origin-left" data-id="element-557">
                      QVC Creator Portal
                    </h3>
                    <p className="text-white/50 font-['Inter']" data-id="element-558">
                      Access the QVC division creator updates and resources.
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Portals */}
        {currentPage === 'portal' && <motion.div key="portal" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-559">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-560" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-561" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-562" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-563">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-564" />
            </div>
            <PortalPage user={agentUser} displayName={agentDisplayName} userRole={agentRole} title="GSF Live Portal" subtitle="Internal communications and updates" collectionName="posts" notesCollectionName="meeting_notes" onSignOut={handleAgentSignOut} onBack={() => setCurrentPage('portal-hub')} data-id="element-565" />
          </motion.div>}
        {currentPage === 'portal-qvc' && <motion.div key="portal-qvc" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-566">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-567" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-568" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-569" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-570">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-571" />
            </div>
            <PortalPage user={agentUser} displayName={agentDisplayName} userRole={agentRole} title="GSF / QVC Live Portal" subtitle="QVC division communications and updates" collectionName="qvc_posts" notesCollectionName="qvc_meeting_notes" onSignOut={handleAgentSignOut} onBack={() => setCurrentPage('portal-hub')} data-id="element-572" />
          </motion.div>}
        {currentPage === 'creator-portal' && <motion.div key="creator-portal" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-573">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-574" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-575" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-576" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-577">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-578" />
            </div>
            <CreatorPortalPage user={creatorUser} displayName={creatorDisplayName} userRole={creatorRole} title="GSF Creator Portal" subtitle="Updates and resources for GSF creators" collectionName="posts" notesCollectionName="meeting_notes" onSignOut={handleCreatorSignOut} onBack={() => setCurrentPage('creator-hub')} data-id="element-579" />
          </motion.div>}
        {currentPage === 'creator-portal-qvc' && <motion.div key="creator-portal-qvc" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-580">
            <img src="https://cdn.magicpatterns.com/uploads/tYoYxUnRaAXde8ACcvqDC5/file_000000008e0c722f98a57c1e17d4b26a.png" alt="Background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-581" />
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-582" />
            <div className="fixed inset-x-0 bottom-0 h-[45%] neon-floor-glow pointer-events-none z-[1]" data-id="element-583" />
            <div className="fixed inset-0 pointer-events-none z-[2]" data-id="element-584">
              <div className="streaming-light-sweep absolute top-0 left-0 w-[30%] h-full bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" data-id="element-585" />
            </div>
            <CreatorPortalPage user={creatorUser} displayName={creatorDisplayName} userRole={creatorRole} title="QVC Creator Portal" subtitle="Updates and resources for QVC creators" collectionName="qvc_posts" notesCollectionName="qvc_meeting_notes" onSignOut={handleCreatorSignOut} onBack={() => setCurrentPage('creator-hub')} data-id="element-586" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}